// ── FX rates (used by CAPEX multi-currency) ──

function getFxToBase(fromCurrency, baseCurrency, assumptions) {
  // Returns multiplier to convert fromCurrency(M) → baseCurrency(M)
  if (fromCurrency === baseCurrency) return 1;

  const {
    jpyPerSgd = 111, jpyPerUsd = 150, jpyPerEur = 163,
    jpyPerGbp = 190, jpyPerThb = 4.1, jpyPerIdr = 0.0094, jpyPerMvr = 9.7,
  } = assumptions || {};

  const toJpy = {
    JPY: 1, SGD: jpyPerSgd, USD: jpyPerUsd, EUR: jpyPerEur,
    GBP: jpyPerGbp, THB: jpyPerThb, IDR: jpyPerIdr, MVR: jpyPerMvr,
  };

  const fromToJpy = toJpy[fromCurrency] || 1;
  const baseToJpy = toJpy[baseCurrency] || 1;
  return fromToJpy / baseToJpy;
}

// ── CAPEX ──

export function calcCapexTotal(categories, contingencyPct = 0.1, assumptions, baseCurrency) {
  const byCategory = {};
  const byCategoryNative = {};
  const byPhase = { phase1: 0, phase2: 0, phase3: 0 };
  let subtotal = 0;

  for (const cat of categories) {
    const catCurrency = cat.currency || baseCurrency || 'JPY';
    const fx = baseCurrency ? getFxToBase(catCurrency, baseCurrency, assumptions) : 1;

    let catTotalNative = 0;
    for (const item of cat.items || []) {
      const p1 = Number(item.phase1) || 0;
      const p2 = Number(item.phase2) || 0;
      const p3 = Number(item.phase3) || 0;
      catTotalNative += p1 + p2 + p3;
      byPhase.phase1 += p1 * fx;
      byPhase.phase2 += p2 * fx;
      byPhase.phase3 += p3 * fx;
    }
    const catTotalConverted = catTotalNative * fx;
    byCategory[cat.name] = catTotalConverted;
    byCategoryNative[cat.name] = { total: catTotalNative, currency: catCurrency };
    subtotal += catTotalConverted;
  }

  const contingencyAmt = subtotal * contingencyPct;
  const total = subtotal + contingencyAmt;

  return { subtotal, contingencyAmt, total, byCategory, byCategoryNative, byPhase };
}

// ── OPEX ──

export function calcOpexTotal(categories, miscPct = 0.05) {
  const byCategory = {};
  let subtotal = 0;

  for (const cat of categories) {
    let catTotal = 0;
    for (const item of cat.items) {
      catTotal += Number(item.annual) || 0;
    }
    byCategory[cat.name] = catTotal;
    subtotal += catTotal;
  }

  const miscAmt = subtotal * miscPct;
  const total = subtotal + miscAmt;

  return { subtotal, miscAmt, total, byCategory };
}

// ── ROI / 10-Year P&L Model ──

function calcAnnualRevenue(assumptions, utilisation) {
  const {
    seasonDays = 120,
    dailyCapacity = 1500,
    liftPassFull = 6500,
    liftPassHalf = 4200,
    pctFullDay = 0.65,
    pctHalfDay = 0.25,
    accomPerVisitor = 12000,
    pctAccom = 0.3,
    fnbPerVisitor = 3500,
    skiSchoolPerVisitor = 8000,
    pctSkiSchool = 0.2,
    retailPerVisitor = 2000,
    seasonPassBase = 80,
  } = assumptions;

  const visitors = dailyCapacity * utilisation * seasonDays;

  const liftRevenue =
    visitors * pctFullDay * liftPassFull +
    visitors * pctHalfDay * liftPassHalf;
  const accomRevenue = visitors * pctAccom * accomPerVisitor;
  const fnbRevenue = visitors * fnbPerVisitor;
  const skiSchoolRevenue = visitors * pctSkiSchool * skiSchoolPerVisitor;
  const retailRevenue = visitors * retailPerVisitor;
  const seasonPassRevenue = seasonPassBase;

  // All values in JPY millions
  const totalRevenue =
    (liftRevenue + accomRevenue + fnbRevenue + skiSchoolRevenue + retailRevenue) /
      1e6 +
    seasonPassRevenue;

  return totalRevenue;
}

const UTILISATION_RAMP = [0.35, 0.45, 0.52, 0.6, 0.6, 0.6, 0.6, 0.6, 0.6, 0.6];

export function calcROI(capexTotal, opexBaseTotal, assumptions) {
  const {
    revGrowth = 0.03,
    opexInflation = 0.025,
    wacc = 0.1,
    taxRate = 0.3,
    debtPct = 0.4,
    interestRate = 0.03,
    deprecYears = 25,
  } = assumptions;

  const annualDeprec = capexTotal / deprecYears;
  const debtAmount = capexTotal * debtPct;
  const annualInterest = debtAmount * interestRate;

  const rows = [];
  let cumulativeFCF = -capexTotal;

  for (let yr = 1; yr <= 10; yr++) {
    const util = UTILISATION_RAMP[yr - 1];
    const baseRevenue = calcAnnualRevenue(assumptions, util);
    const revenue = baseRevenue * Math.pow(1 + revGrowth, yr - 1);

    const opex = opexBaseTotal * Math.pow(1 + opexInflation, yr - 1);
    const ebitda = revenue - opex;
    const ebitdaMargin = revenue > 0 ? ebitda / revenue : 0;
    const ebit = ebitda - annualDeprec;
    const ebt = ebit - annualInterest;
    const tax = Math.max(0, ebt * taxRate);
    const netProfit = ebt - tax;
    const fcf = netProfit + annualDeprec;
    cumulativeFCF += fcf;

    rows.push({
      year: yr,
      revenue,
      opex,
      ebitda,
      ebitdaMargin,
      depreciation: annualDeprec,
      ebit,
      interest: annualInterest,
      ebt,
      tax,
      netProfit,
      fcf,
      cumulativeFCF,
    });
  }

  // Payback year
  let paybackYear = null;
  for (const r of rows) {
    if (r.cumulativeFCF >= 0) {
      paybackYear = r.year;
      break;
    }
  }

  // NPV
  const npv =
    -capexTotal +
    rows.reduce((sum, r) => sum + r.fcf / Math.pow(1 + wacc, r.year), 0);

  // IRR via Newton's method
  const irr = calcIRR(capexTotal, rows.map((r) => r.fcf));

  // Average EBITDA margin
  const avgEbitdaMargin =
    rows.reduce((s, r) => s + r.ebitdaMargin, 0) / rows.length;

  return { rows, paybackYear, npv, irr, avgEbitdaMargin, capexTotal };
}

function calcIRR(initialInvestment, cashFlows, guess = 0.1, maxIter = 100, tol = 1e-7) {
  let rate = guess;

  for (let i = 0; i < maxIter; i++) {
    let npv = -initialInvestment;
    let dNpv = 0;

    for (let t = 0; t < cashFlows.length; t++) {
      const cf = cashFlows[t];
      const disc = Math.pow(1 + rate, t + 1);
      npv += cf / disc;
      dNpv -= (t + 1) * cf / Math.pow(1 + rate, t + 2);
    }

    if (Math.abs(dNpv) < 1e-14) break;

    const newRate = rate - npv / dNpv;
    if (Math.abs(newRate - rate) < tol) return newRate;
    rate = newRate;
  }

  return rate;
}

// ── Sensitivity ──

export function sensitivityScenarios(capexTotal, opexTotal) {
  const scenarios = [
    { label: 'Bear', revMultiplier: 0.7 },
    { label: 'Conservative', revMultiplier: 0.85 },
    { label: 'Base', revMultiplier: 1.0 },
    { label: 'Optimistic', revMultiplier: 1.15 },
    { label: 'Bull', revMultiplier: 1.3 },
  ];

  return scenarios.map((s) => {
    // Simplified: assume steady-state revenue at 60% utilisation
    const baseRevenue = capexTotal * 0.25 * s.revMultiplier; // rough proxy
    const ebitda = baseRevenue - opexTotal;
    const margin = baseRevenue > 0 ? ebitda / baseRevenue : 0;
    const simplePayback = ebitda > 0 ? capexTotal / ebitda : Infinity;

    return {
      label: s.label,
      revenue: baseRevenue,
      ebitda,
      margin,
      simplePayback,
    };
  });
}

// ── Currency conversion ──

export function convertCurrency(valueJpyM, targetCurrency, assumptions) {
  const {
    jpyPerSgd = 111,
    jpyPerUsd = 150,
    jpyPerEur = 163,
    jpyPerGbp = 190,
    jpyPerThb = 4.1,
    jpyPerIdr = 0.0094,
    jpyPerMvr = 9.7,
  } = assumptions;

  const rates = {
    SGD: jpyPerSgd,
    USD: jpyPerUsd,
    EUR: jpyPerEur,
    GBP: jpyPerGbp,
    THB: jpyPerThb,
    IDR: jpyPerIdr,
    MVR: jpyPerMvr,
  };

  if (targetCurrency === 'JPY') return valueJpyM;
  const rate = rates[targetCurrency];
  return rate ? valueJpyM / rate : valueJpyM;
}

const SYMBOLS = {
  JPY: '¥', SGD: 'S$', USD: '$', EUR: '€', GBP: '£', THB: '฿', IDR: 'Rp', MVR: 'Rf',
};

export function currencySymbol(currency) {
  return SYMBOLS[currency] || currency;
}

export function fmtNum(value, decimals = 2) {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatCurrency(valueJpyM, currency, assumptions, decimals = 1) {
  const converted = convertCurrency(valueJpyM, currency, assumptions);
  const sym = currencySymbol(currency);
  return `${sym}${fmtNum(converted, decimals)}M`;
}

// Auto-scale: values under 1M show in K, over 1M show in M
export function fmtAuto(valueInM, currency) {
  const sym = currencySymbol(currency);
  const abs = Math.abs(valueInM);
  if (abs === 0) return `${sym}0`;
  if (abs < 0.01) {
    // Show raw number (under 10K)
    const raw = valueInM * 1e6;
    return `${sym}${fmtNum(raw, 0)}`;
  }
  if (abs < 1) {
    // Show in K
    const inK = valueInM * 1000;
    return `${sym}${fmtNum(inK, 1)}K`;
  }
  // Show in M
  return `${sym}${fmtNum(valueInM, 1)}M`;
}
